import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const probateCasesApi = createApi({
  reducerPath: "probateCasesApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ProbateCase", "ProbateCaseStats"],
  endpoints: (build) => ({
    getAllCases: build.query({
      query: ({ status, motivation, search, starred, page = 1, limit = 100 } = {}) => ({
        url: "/probate/cases",
        method: "GET",
        params: { status, motivation, search, starred, page, limit },
      }),
      providesTags: ["ProbateCase"],
    }),

    getCaseById: build.query({
      query: (id) => ({ url: `/probate/cases/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "ProbateCase", id }],
    }),

    createCase: build.mutation({
      query: (caseData) => ({ url: "/probate/cases", method: "POST", body: caseData }),
      invalidatesTags: ["ProbateCase", "ProbateCaseStats"],
    }),

    updateCase: build.mutation({
      query: ({ id, ...caseData }) => ({ url: `/probate/cases/${id}`, method: "PUT", body: caseData }),
      invalidatesTags: (result, error, { id }) => [{ type: "ProbateCase", id }, "ProbateCase", "ProbateCaseStats"],
    }),

    deleteCase: build.mutation({
      query: (id) => ({ url: `/probate/cases/${id}`, method: "DELETE" }),
      invalidatesTags: ["ProbateCase", "ProbateCaseStats"],
    }),

    updateCaseStatus: build.mutation({
      query: ({ id, status }) => ({ url: `/probate/cases/${id}/status`, method: "PATCH", body: { status } }),
      invalidatesTags: (result, error, { id }) => [{ type: "ProbateCase", id }, "ProbateCase", "ProbateCaseStats"],
    }),

    toggleCaseStar: build.mutation({
      query: (id) => ({ url: `/probate/cases/${id}/star`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [{ type: "ProbateCase", id }, "ProbateCase"],
    }),

    getCaseStats: build.query({
      query: () => ({ url: "/probate/cases/stats", method: "GET" }),
      providesTags: ["ProbateCaseStats"],
    }),

    getCaseMilestones: build.query({
      query: (id) => ({ url: `/probate/cases/${id}/milestones`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "ProbateCase", id }],
    }),

    updateCaseMilestones: build.mutation({
      query: ({ id, milestones, filingDate }) => ({
        url: `/probate/cases/${id}/milestones`,
        method: "PATCH",
        body: { milestones, filingDate },
      }),
      // Intentionally no invalidatesTags — local state is source of truth after save
    }),

    getShareDetails: build.query({
      query: (id) => ({ url: `/probate/cases/${id}/share`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "ProbateCase", id }],
    }),

    generateShareToken: build.mutation({
      // Accepts { id, sharePrefs } — sharePrefs is the agent's choice of
      // what to expose on the public timeline. Backend validates and
      // logs PR-consent attestation when prContactConsent is granted.
      query: ({ id, sharePrefs }) => ({
        url: `/probate/cases/${id}/share`,
        method: "POST",
        body: { sharePrefs },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "ProbateCase", id }, "ProbateCase"],
    }),

    revokeShareToken: build.mutation({
      query: (id) => ({ url: `/probate/cases/${id}/share`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [{ type: "ProbateCase", id }, "ProbateCase"],
    }),
  }),
});

export const {
  useGetAllCasesQuery,
  useGetCaseByIdQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useDeleteCaseMutation,
  useUpdateCaseStatusMutation,
  useToggleCaseStarMutation,
  useGetCaseStatsQuery,
  useGetCaseMilestonesQuery,
  useUpdateCaseMilestonesMutation,
  useGetShareDetailsQuery,
  useLazyGetShareDetailsQuery,
  useGenerateShareTokenMutation,
  useRevokeShareTokenMutation,
} = probateCasesApi;
